/* eslint-disable eqeqeq, no-param-reassign */
import d3 from 'd3';
import * as Config from 'es6/components/memory-bank/config';
import BaseVisual from 'es6/components/memory-bank/visuals/base-visual';
import DifficultyLayout from 'es6/components/memory-bank/layouts/difficulty';

// Used to adjust the rate of movement on each axis.
const ForceMultiplier = {
  X: 0.4,
  Y: 0.2,
};

// Difficulty is number based from 0-3. This is a map between
// index and section.
const DIFFICULTY_LOOKUP = ['easy', 'moderate', 'hard', 'very-hard'];

/**
 * Helper function for the show() function.
 *
 * Function for applying a collision force between roundish objects.
 * Objects must have x, y, and radius defined.
 *
 * Originally based on http://bl.ocks.org/mbostock/1748247
 *
 * @return {Function}  Method to apply on each node
 */
function collide(force) {
  const quadtree = d3.geom.quadtree(force.nodes());

  return (d) => {
    let r = Config.Circle.RADIUS;
    const nx1 = d.x - r;
    const nx2 = d.x + r;
    const ny1 = d.y - r;
    const ny2 = d.y + r;

    quadtree.visit((quad, x1, y1, x2, y2) => {
      if (quad.point && (quad.point != d)) {
        let x = d.x - quad.point.x;
        let y = d.y - quad.point.y;
        let l = Math.sqrt(x * x + y * y);
        r = Config.Circle.RADIUS * 2;

        if (l < r) {
          l = (l - r) / l * 0.5;

          d.x -= x *= l;
          d.y -= y *= l;

          quad.point.x += x;
          quad.point.y += y;
        }
      }

      return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
    });
  };
}

/**
 * @requires d3
 * @requires d3-plugins
 */
export class Difficulty extends BaseVisual {
  constructor(...args) {
    super(...args);
    this._layout = new DifficultyLayout(this._memoryBankPlot.layoutsContainer);
  }

  hide() {
    super.hide();
    this._layout.hide();
  }

  show() {
    super.show();
    this._layout.show();

    // Store a reference to sections, so it can be used inside methods.
    const sections = this._layout.sections;
    const visibleSelection = this._getVisible();

    // Add some randomization to unbundle collapsed nodes.
    visibleSelection.each(function (d) {
      const transform = d3.transform(d3.select(this).attr('transform'));
      let [x, y] = transform.translate;

      x += Math.random() - 0.5;
      y += Math.random() - 0.5;

      d.x = x;
      d.y = y;
    });

    // Create a force layout and tick through it to get the final positions
    // of the nodes.
    const force = d3.layout.force()
      .gravity(0)
      .charge(0)
      .friction(0.7)
      .nodes(this._memoryBankPlot.nodes.filter(this._isNodeVisible))
      .on('tick', (e) => {
        const alpha = e.alpha;
        const k = {
          x: ForceMultiplier.X * alpha,
          y: ForceMultiplier.Y * alpha,
        };

        visibleSelection
          .each((d, i) => {
            const sectionName = DIFFICULTY_LOOKUP[d.difficultyBucket];
            const section = sections[sectionName];

            d.y += (section.originY - d.y) * k.y;
            d.x += (section.originX - d.x) * k.x;
          })
          .each(collide(force));
      });

    // Simulate ticks.
    force.start();
    for (let i = 0; i < 600; i++) {
      force.tick();
    }

    // Update all the nodes with the corresponding location.
    visibleSelection
      .transition()
      .duration(Config.Animation.SLOWEST)
      .delay(d => Math.random() * 200)
      .attr('transform', d => this._translate(d.x, d.y));

    // Set tooltips and move hidden nodes.
    this._setTooltips();
    this._moveHiddenNodes();
  }

  /**
   * No special transition happens here, just call the show method.
   */
  update() {
    this.show();
  }
}

export default Difficulty;
